
import Vue from 'vue';

import AdsLayout from '@root/common/components/layout/AdsLayout.vue';
import GridSkeletonLoader from '@root/common/components/base/loader/GridSkeletonLoader.vue';
import isMobileView from '@root/common/utils/isMobileView';
import { K5AStreamRenderReadyData, KilkayaWindow } from '@root/modules/kilkaya/types/kilkaya';

interface Data {
  hasLoadedOnce: boolean;
  isMobileView: boolean;
  pendingSetupPromise: Promise<void> | undefined;
}

interface Methods {
  handleStreamReady: (event: CustomEvent<K5AStreamRenderReadyData>) => void;
  setupStream: () => void;
}

interface Props {
  slotName: string;
}

declare const window: KilkayaWindow;

// Make Vue ignore k5a-stream markup
Vue.config.ignoredElements = [/^k5a-/];

export default Vue.extend<Data, Methods, unknown, Props>({
  components: {
    AdsLayout,
    GridSkeletonLoader,
  },
  props: {
    slotName: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      hasLoadedOnce: false,
      isMobileView: false,
      pendingSetupPromise: undefined,
    };
  },
  methods: {
    handleStreamReady(event: CustomEvent<K5AStreamRenderReadyData>) {
      const detail = event.detail;

      if (detail.slotName && detail.slotName !== this.slotName) {
        return;
      }

      this.hasLoadedOnce = true;
    },
    setupStream(): Promise<void> {
      if (this.pendingSetupPromise) {
        return this.pendingSetupPromise;
      }

      this.pendingSetupPromise = window.k5aStreamReadyPromise
        .then(() => {
          // Add 1s timeout before invoking kilkaya?.pilash?.hardInit(), which is suspected to be a victim of race condition
          setTimeout(() => {
            window.kilkaya?.pageData?.reRead(); // syncs window.k5aMeta into pageData
            window.kilkaya?.pilash?.hardInit(); // clears render cache and renders existing <k5a-stream> elements on the page
          }, 1000);
        })
        .finally(() => {
          this.pendingSetupPromise = undefined;
        });

      return this.pendingSetupPromise;
    },
  },
  beforeMount() {
    document.addEventListener('K5A:StreamRenderReady', this.handleStreamReady);
  },
  async mounted() {
    this.isMobileView = isMobileView();

    await this.$nextTick();

    this.setupStream();
  },
  beforeDestroy() {
    document.removeEventListener('K5A:StreamRenderReady', this.handleStreamReady);
  },
});
